// Signing log (roadmap 4.2): every estimate and storage contract sent to sign by link
// (functions/signLinks.js) — who signed, when, from where, the signed copy (PDF), and for the ones
// still waiting: copy the link, stop it, or email a reminder (from the company account, with the
// link). Opened from the Estimates list and from Storage Contracts › Send reminders to unsigned.

import { functions, storage } from './initFirebase';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { toastMessage, escapeHTML } from './utilities';
import { formatMoney } from './money';
import { sendMail, businessProfile } from './mailClient';

const esc = v => escapeHTML(v == null ? '' : String(v));
const call = (name, data) => httpsCallable(functions, name, { timeout: 60000 })(data).then(r => r.data);
const errText = e => String(e?.message || e).replace(/^FirebaseError:\s*/, '');
const day = ms => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const STATES = [['all', 'All'], ['open', 'Waiting'], ['signed', 'Signed'], ['declined', 'Declined'], ['closed', 'Expired / stopped']];
const PILL = { open: ['Waiting', 'inv-pill-open'], signed: ['Signed', 'inv-pill-paid'], declined: ['Declined', 'inv-pill-overdue'], expired: ['Expired', 'inv-pill-draft'], revoked: ['Stopped', 'inv-pill-draft'] };

const L = { rows: [], kind: 'all', state: 'all', picked: new Set(), open: null, busy: false };

/** Open the log. kind: all | estimate | contract | repair; state: all | open | signed | declined | closed; pickAll: tick every waiting one. */
export async function openSignLog({ kind = 'all', state = 'all', pickAll = false } = {}) {
  document.getElementById('sign-log')?.remove();
  Object.assign(L, { kind, state, picked: new Set(), open: null });
  const wrap = document.createElement('div');
  wrap.id = 'sign-log';
  wrap.className = 'inv-root inv-backdrop';
  wrap.innerHTML = `
    <div class="inv-panel inv-list-panel" role="dialog" aria-modal="true" aria-labelledby="sign-log-title">
      <div class="inv-head">
        <div class="inv-title" id="sign-log-title">Signing log</div>
        <button type="button" class="inv-x" data-s="close" aria-label="Close">&times;</button>
      </div>
      <div class="inv-tools">
        <div class="inv-seg" role="tablist">${STATES.map(([v, l]) => `<button type="button" class="inv-seg-btn" data-state="${v}">${l}</button>`).join('')}</div>
        <select class="sl-kind" data-s="kind" aria-label="Documents">
          <option value="all">All documents</option><option value="estimate">Estimates</option><option value="contract">Storage contracts</option><option value="repair">Canvas repairs</option>
        </select>
        <span class="inv-spacer"></span>
        <button type="button" class="inv-btn inv-primary inv-small" data-s="remind" disabled>Send reminders (0)</button>
      </div>
      <div class="inv-body" id="sign-log-list"><div class="inv-muted">Loading…</div></div>
      <div class="inv-muted sl-progress" data-s="progress"></div>
    </div>`;
  document.body.appendChild(wrap);
  wrap.querySelector('[data-s="kind"]').value = kind;
  wrap.addEventListener('click', onClick);
  wrap.addEventListener('change', onChange);
  wrap.addEventListener('keydown', e => { if (e.key === 'Escape') wrap.remove(); });
  try {
    L.rows = (await call('listSignRequests')).requests || [];
  } catch (err) {
    wrap.querySelector('#sign-log-list').innerHTML = `<div class="inv-warn">${esc(errText(err))}</div>`;
    return;
  }
  if (pickAll) visible().filter(r => r.state === 'open').forEach(r => L.picked.add(r.id));
  render();
}

function visible() {
  return L.rows.filter(r => (L.kind === 'all' || r.kind === L.kind)
    && (L.state === 'all' || (L.state === 'closed' ? (r.state === 'expired' || r.state === 'revoked') : r.state === L.state)));
}

function render() {
  const root = document.getElementById('sign-log');
  if (!root) return;
  root.querySelectorAll('[data-state]').forEach(b => b.classList.toggle('inv-on', b.dataset.state === L.state));
  const rows = visible();
  [...L.picked].forEach(id => { if (!rows.some(r => r.id === id && r.state === 'open')) L.picked.delete(id); });
  const btn = root.querySelector('[data-s="remind"]');
  btn.textContent = `Send reminders (${L.picked.size})`;
  btn.disabled = !L.picked.size || L.busy;
  const box = root.querySelector('#sign-log-list');
  if (!rows.length) { box.innerHTML = '<div class="inv-empty">Nothing here. Links appear once an estimate or storage contract is emailed to sign.</div>'; return; }
  box.innerHTML = `<table class="inv-lines sl-table"><thead><tr><th class="inv-c"></th><th>Document</th><th>Customer</th><th>Status</th><th>Sent</th><th>Signed</th><th></th></tr></thead><tbody>
    ${rows.map(r => {
      const [label, cls] = PILL[r.state] || [r.state, ''];
      return `<tr data-row="${esc(r.id)}">
        <td class="inv-c">${r.state === 'open' ? `<input type="checkbox" data-pick="${esc(r.id)}"${L.picked.has(r.id) ? ' checked' : ''}${r.email ? '' : ' disabled title="No email address on file"'} aria-label="Remind about ${esc(r.title)}">` : ''}</td>
        <td><b>${esc(r.title)}</b><div class="inv-muted">${esc(r.subtitle)} · ${formatMoney(r.totalCents)}</div></td>
        <td>${esc(r.customerName)}${r.email ? '' : ' <span class="inv-warn">no email</span>'}</td>
        <td><span class="inv-pill ${cls}">${esc(label)}</span></td>
        <td>${esc(day(r.createdAt))}<div class="inv-muted">${esc(r.createdBy)}${r.reminderCount ? ` · reminded ${r.reminderCount}×, last ${esc(day(r.remindedAt))}` : ''}</div></td>
        <td>${r.signedAt ? `${esc(r.signedName || '')}<div class="inv-muted">${esc(day(r.signedAt))}</div>` : r.state === 'open' ? `<span class="inv-muted">link works until ${esc(day(r.expiresAt))}</span>` : ''}</td>
        <td class="sl-acts">
          ${r.pdfPath ? `<button type="button" class="inv-link" data-pdf="${esc(r.id)}">Signed copy</button>` : ''}
          ${r.signedAt ? `<button type="button" class="inv-link" data-detail="${esc(r.id)}">${L.open === r.id ? 'Hide' : 'Details'}</button>` : ''}
          ${r.state === 'open' ? `<button type="button" class="inv-link" data-copy="${esc(r.id)}">Copy link</button> <button type="button" class="inv-link" data-stop="${esc(r.id)}">Stop link</button>` : ''}
        </td></tr>
        ${L.open === r.id ? `<tr class="sl-detail"><td></td><td colspan="6">
          <div><b>${r.decision === 'decline' ? 'Declined' : r.kind === 'repair' ? 'Answered' : 'Signed'}</b> by ${esc(r.signedName || '—')} on ${esc(new Date(r.signedAt).toLocaleString())}</div>
          <div>Internet address: ${esc(r.ip || 'unknown')}</div>
          <div>Browser: ${esc(r.userAgent || 'unknown')}</div>
          <div>Document fingerprint (SHA-256): <code>${esc(r.docHash || '')}</code></div>
        </td></tr>` : ''}`;
    }).join('')}
  </tbody></table>`;
}

function onChange(e) {
  if (e.target.dataset.s === 'kind') { L.kind = e.target.value; render(); }
  const id = e.target.dataset.pick;
  if (id) { e.target.checked ? L.picked.add(id) : L.picked.delete(id); render(); }
}

async function onClick(e) {
  const root = document.getElementById('sign-log');
  if (e.target === root || e.target.closest('[data-s="close"]')) return root.remove();
  const st = e.target.closest('[data-state]')?.dataset.state;
  if (st) { L.state = st; return render(); }
  const t = e.target.closest('button');
  if (!t) return;
  const row = id => L.rows.find(r => r.id === id);
  if (t.dataset.detail) { L.open = L.open === t.dataset.detail ? null : t.dataset.detail; return render(); }
  if (t.dataset.pdf) {
    const tab = window.open('about:blank', '_blank');
    try {
      const url = await getDownloadURL(storageRef(storage, row(t.dataset.pdf).pdfPath));
      if (tab) tab.location.href = url; else window.location.href = url;
    } catch (err) { tab?.close(); toastMessage(errText(err), 'red', 6000); }
    return;
  }
  if (t.dataset.copy) {
    const url = row(t.dataset.copy).url;
    await navigator.clipboard.writeText(url).catch(() => window.prompt('Signing link (copy it):', url));
    return toastMessage('Signing link copied', 'green');
  }
  if (t.dataset.stop) {
    if (!confirm('Stop this link? The customer’s page will say it was replaced. Email the document again to send a new one.')) return;
    try {
      await call('revokeSignLink', { id: t.dataset.stop });
      const r = row(t.dataset.stop);
      Object.assign(r, { state: 'revoked', url: null });
      toastMessage('Link stopped', 'green');
    } catch (err) { toastMessage(errText(err), 'red', 6000); }
    return render();
  }
  if (t.dataset.s === 'remind') return sendReminders([...L.picked]);
}

/** Email each a reminder with their link (sendMail, from the company account), and note it. */
async function sendReminders(ids) {
  const root = document.getElementById('sign-log');
  const prog = root.querySelector('[data-s="progress"]');
  if (!confirm(`Email a reminder to sign to ${ids.length} customer${ids.length === 1 ? '' : 's'}?`)) return;
  L.busy = true;
  render();
  const biz = await businessProfile();
  const done = { sent: 0, failed: [] };
  for (const [i, id] of ids.entries()) {
    const r = L.rows.find(x => x.id === id);
    prog.textContent = `Sending ${i + 1} of ${ids.length}… ${r.customerName}`;
    const first = String(r.customerName || '').split(' ')[0];
    const body = `${first ? `Hi ${first},` : 'Hello,'}\n\nThis is a friendly reminder that your ${r.title} is waiting for your signature. You can review and sign it online.\n\n{sign_link}\n\nThe link works until ${day(r.expiresAt)}. Reply to this email or give us a call with any questions.\n\nThank you,`;
    try {
      await sendMail({
        to: r.email, subject: `Reminder: please sign your ${r.title}${biz.name ? ` from ${biz.name}` : ''}`,
        layout: { text: body, links: { sign_link: r.url } },
        customerId: r.customerId, docType: 'reminder', docLabel: `Sign reminder: ${r.title}`,
      });
      await call('markSignReminded', { id }).catch(() => {});
      r.reminderCount = (r.reminderCount || 0) + 1;
      r.remindedAt = Date.now();
      done.sent++;
    } catch (err) {
      done.failed.push(`${r.customerName}: ${errText(err)}`);
    }
  }
  L.busy = false;
  L.picked = new Set();
  prog.innerHTML = `Reminders sent: ${done.sent}.${done.failed.length ? ` <span class="inv-warn">Failed: ${esc(done.failed.join('; '))}</span>` : ''}`;
  toastMessage(`Reminders sent: ${done.sent}`, done.failed.length ? 'red' : 'green', 5000);
  render();
}
